import MoneyIcon from '@mui/icons-material/AttachMoney'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import DownloadIcon from '@mui/icons-material/Download'
import FileOpenIcon from '@mui/icons-material/FileOpen'
import HailIcon from '@mui/icons-material/Hail'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import StarIcon from '@mui/icons-material/Star'
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { green, grey } from '@mui/material/colors'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { ColumnDef } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Earning } from '../../types/interfaces'
import { DriverLetter } from '../components/DriverLetter'
import { EarningsChart } from '../components/EarningsChart'
import { EarningsStatsCard } from '../components/EarningsStatsCard'
import { EarningsChartSkeleton } from '../components/loading/EarningsChartSkeleton'
import { RidesTable } from '../components/RidesTable'
import { RidesTableMenu } from '../components/RidesTableMenu'
import { SortableTaleHeader } from '../components/SortableTableHeader'
import { formatCurrency } from '../utils/number'
import { useEarnings } from './hooks/useEarnings'
import { useNavigationAndSortEarningsTable } from './hooks/useNavigationAndSortEarningsTable'

const Earnings = () => {
  const { handleChangePagination, handleSort, pagination, sortParams } =
    useNavigationAndSortEarningsTable()

  const {
    earningLetter,
    earnings,
    earningsPage,
    earningsPerPage,
    earningsStats,
    isLoadingEarningLetter,
    isLoadingEarnings,
    isLoadingStats,
    isRequestingDriverLetter,
    requestDriverLetter,
    totalEarnings,
  } = useEarnings({
    limit: pagination.pageSize,
    page: pagination.page,
    sort: sortParams,
  })

  const { t } = useTranslation()

  const headers: ColumnDef<Earning>[] = useMemo(
    () => [
      {
        accessorKey: 'start_date',
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_earnings_headers.start_date')}
            onSort={handleSort}
          />
        ),
      },
      {
        accessorKey: 'end_date',
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_earnings_headers.end_date')}
            onSort={handleSort}
          />
        ),
      },
      {
        accessorKey: 'net_pay',
        cell: ({ getValue }) => formatCurrency(getValue<number>()),
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_earnings_headers.total_earned')}
            onSort={handleSort}
          />
        ),
      },
      {
        cell: ({ row }) => {
          const rideId = row.original.id
          return <RidesTableMenu rideId={rideId} />
        },
        id: 'actions',
      },
    ],
    [handleSort, t]
  )

  const getDriverReportButton = useCallback(() => {
    if (isLoadingEarningLetter) {
      return (
        <Skeleton
          animation="wave"
          height={36}
          variant="rectangular"
          width={250}
        />
      )
    }

    if (earningLetter?.is_available) {
      return (
        <PDFDownloadLink
          document={
            <DriverLetter
              averageEarnings={earningLetter?.average ?? 0}
              birthDate={earningLetter?.date_birth ?? ''}
              date={earningLetter?.date ?? ''}
              driverName={earningLetter?.driver ?? ''}
              firstTripDate={earningLetter?.first_trip ?? ''}
              managerEmail={earningLetter?.manager_email ?? ''}
              managerName={earningLetter?.manager ?? ''}
            />
          }
          fileName="driver-letter.pdf"
          style={{ color: 'white', textDecoration: 'none' }}
        >
          {() => (
            <Button endIcon={<DownloadIcon />} variant="contained">
              {t('earnings.download_driver_letter')}
            </Button>
          )}
        </PDFDownloadLink>
      )
    }

    return (
      <Tooltip
        arrow
        title={
          earningLetter?.is_pending
            ? t('earnings.driver_letter_waiting_approval_tooltip')
            : t('earnings.request_driver_letter_tooltip')
        }
      >
        <span>
          <Button
            disabled={
              !earningLetter?.can_request ||
              isRequestingDriverLetter ||
              earningLetter?.is_pending
            }
            endIcon={
              earningLetter?.is_pending ? <RotateRightIcon /> : <FileOpenIcon />
            }
            onClick={() => requestDriverLetter()}
            variant="contained"
          >
            <Typography variant="button">
              {earningLetter?.is_pending
                ? t('earnings.waiting_driver_letter_approval')
                : t('earnings.request_driver_letter')}
            </Typography>
          </Button>
        </span>
      </Tooltip>
    )
  }, [
    earningLetter,
    isLoadingEarningLetter,
    isRequestingDriverLetter,
    requestDriverLetter,
    t,
  ])

  return (
    <Container
      sx={{
        marginBottom: {
          md: 0,
          xs: 4,
        },
      }}
    >
      <Grid container rowGap={4} sx={{ p: 2 }}>
        {import.meta.env.VITE_ENVIRONMENT_NAME !== 'main' && (
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="end">
              {getDriverReportButton()}
            </Stack>
          </Grid>
        )}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography sx={{ color: grey[800], mb: 2 }} variant="h5">
                {t('earnings.stats.title')}
              </Typography>
              <Grid alignItems="stretch" container spacing={3}>
                <Grid item lg={3} xs={12}>
                  <EarningsStatsCard
                    avatarStyle={{
                      backgroundColor: green[600],
                    }}
                    cardStyle={{
                      boxShadow:
                        'rgba(0, 0, 0, 0.06) 0px 5px 22px 0px, rgba(0, 128, 0, 0.5) 0px 0px 0px 1px',
                    }}
                    icon={<StarIcon />}
                    loading={isLoadingStats}
                    title={t('earnings.stats.driver_rating')}
                    titleStyle={{
                      color: green[600],
                    }}
                    value={earningsStats?.rating ?? 0}
                    valueStyle={{
                      color: green[600],
                    }}
                  />
                </Grid>
                <Grid item lg={3} xs={12}>
                  <EarningsStatsCard
                    icon={
                      <HailIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingStats}
                    title={t('earnings.stats.total_rides')}
                    value={earningsStats?.total_rides ?? 0}
                  />
                </Grid>
                <Grid item lg={3} xs={12}>
                  <EarningsStatsCard
                    icon={
                      <MoneyIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingStats}
                    title={t('earnings.stats.total_earnings')}
                    value={formatCurrency(earningsStats?.total_earnings ?? 0)}
                  />
                </Grid>
                <Grid item lg={3} xs={12}>
                  <EarningsStatsCard
                    icon={
                      <CreditCardIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingStats}
                    title={t('earnings.stats.total_tips')}
                    value={formatCurrency(earningsStats?.total_tips ?? 0)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography sx={{ color: grey[800], mb: 2 }} variant="h5">
                {t('earnings.weekly_earnings')}
              </Typography>

              {isLoadingStats ? (
                <EarningsChartSkeleton />
              ) : (
                <EarningsChart
                  data={earningsStats?.earnings_last_8_weeks ?? []}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Stack alignItems="baseline" direction="row">
                <Typography sx={{ color: grey[800], mb: 2 }} variant="h5">
                  {t('earnings.latest_earnings')}
                </Typography>
              </Stack>
              <RidesTable
                columns={headers}
                containerStyle={{ height: 500 }}
                currentPage={earningsPage}
                data={earnings ?? []}
                isLoading={isLoadingEarnings}
                onChangePagination={handleChangePagination}
                pageSize={earningsPerPage}
                totalItems={totalEarnings}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default Earnings
